export class PathUtils {
  static isPathEqual(path1: Editor.Selection.Path, path2: Editor.Selection.Path) {
    // check path
    if (path1.length === path2.length) {
      for (let i = 0; i < path1.length; i++) {
        if (path1[i] != path2[i]) {
          return false;
        }
      }
      return true;
    }

    return false;
  }

  static isValidSelectionPath(
    path: Realtime.Core.RealtimePath | Realtime.Core.AbstractPath,
  ): path is Editor.Selection.Path {
    if (path.length === 0) {
      return true;
    }

    let valid = false;

    let prevKey;
    for (let i = 0; i < path.length; i++) {
      let key = path[i];
      if (key === 'childNodes' || key === 'content') {
        prevKey = key;
        continue;
      } else if (prevKey === 'childNodes' || prevKey === 'content') {
        if (!isNaN(+key) && +key >= 0) {
          prevKey = null;
          valid = true;
          continue;
        } else {
          return false;
        }
      } else {
        // properties path or unknown path
        break;
      }
    }

    return valid;
  }

  static getCommonAncestorPath(
    path1: Realtime.Core.RealtimePath,
    path2: Realtime.Core.RealtimePath,
  ): Editor.Selection.Path {
    let commonPath: Editor.Selection.Path = [];

    let i = 0;
    let lastKey: 'childNodes' | 'content' | null = null;

    while (path1[i] != null && path1[i] == path2[i]) {
      let key = path1[i];
      if (key === 'childNodes') {
        lastKey = key;
      } else if (lastKey != null && !isNaN(+key)) {
        commonPath.push(lastKey);
        commonPath.push(+key);

        lastKey = null;
      }

      i++;
    }

    return commonPath;
  }

  static isChildPath(parentPath: Editor.Selection.Path, childPath: Editor.Selection.Path) {
    let includes: boolean = false;

    if (parentPath.length === 0 && childPath.length !== 0) {
      return true;
    }

    if (parentPath.length > 0 && childPath.length > 0) {
      for (let i = 0; i < parentPath.length; i++) {
        if (parentPath[i] != null && childPath[i] != null) {
          // '==' intended

          if (parentPath[i] == childPath[i]) {
            includes = true;
          } else {
            includes = false;
            break;
          }
        } else {
          includes = false;
          break;
        }
      }
    }

    return includes;
  }

  /**
   * Compares two paths positions
   * 0 if paths are equal,
   * -1 if path1 is before path2,
   * 1 if path1 is after path2
   */
  static comparePath(path1: Editor.Selection.Path, path2: Editor.Selection.Path): number {
    if (path1?.length >= 0 && path2?.length >= 0) {
      let length: number = 0;
      if (path1.length >= path2.length) {
        length = path1.length;
      } else {
        length = path2.length;
      }

      let previousIteration: string | null = null;

      for (let i = 0; i < length; i++) {
        if (path1[i] != null && path2[i] != null) {
          if (
            (path1[i] === 'childNodes' && path2[i] === 'childNodes') ||
            (path1[i] === 'content' && path2[i] === 'content')
          ) {
            previousIteration = path1[i] as string;
          } else if (previousIteration != null) {
            const index1 = +path1[i];
            const index2 = +path2[i];

            if (index1 < index2) {
              // path1 before path2
              return -1;
            } else if (index1 > index2) {
              // path1 after path2
              return 1;
            }

            previousIteration = null;
          } else {
            throw new Error('Invalid path to compare!');
          }
        } else if (path1[i] == null && path2[i] == null) {
          // paths should be equal
          return 0;
        } else if (path1[i] == null) {
          // path1 before path2
          return -1;
        } else if (path2[i] == null) {
          // path1 after path2
          return 1;
        }
      }
    } else {
      throw new Error('Invalid path to compare!');
    }

    return 0;
  }

  /**
   * Returns true if path `a` comes after path `b` in tree order
   */
  static isPathAfter(a: Editor.Selection.Path, b: Editor.Selection.Path): boolean {
    return this.comparePath(a, b) === 1;
  }

  static containsPath(
    start: Editor.Selection.Path,
    end: Editor.Selection.Path,
    path: Editor.Selection.Path,
  ) {
    let cmpStart = PathUtils.comparePath(path, start);
    let cmpEnd = PathUtils.comparePath(path, end);
    if (cmpStart >= 0 && cmpEnd <= 0) {
      return true;
    }
    return false;
  }

  static transformPath(
    basePath: Editor.Selection.Path | null,
    transformPath: Editor.Selection.Path | null,
    action: 'ADD' | 'SUBTRACT' = 'SUBTRACT',
  ): Editor.Selection.Path | null {
    if (basePath == null) {
      return null;
    }

    if (transformPath == null) {
      return basePath;
    }

    let resultPath: Editor.Selection.Path | null = [];

    if (basePath.length >= 0 && transformPath.length >= 0) {
      if (transformPath.length === 0) {
        resultPath = basePath;
      } else {
        let previousIteration: string | null = null;
        let preivousP1Index: number | null = null;
        let keepTransforming = true;

        const length =
          basePath.length > transformPath.length ? basePath.length : transformPath.length;

        for (let i = 0; i < length; i++) {
          const p1 = basePath[i];
          const p2 = transformPath[i];

          if (p1 != null && p2 != null) {
            if (
              (p1 === 'childNodes' && p2 === 'childNodes') ||
              (p1 === 'content' && p2 === 'content')
            ) {
              previousIteration = p1 as string;
              resultPath.push(p1);
            } else if (previousIteration != null) {
              if (keepTransforming) {
                let index = -1;
                if (action === 'SUBTRACT') {
                  index = +p1 - +p2;
                  if (index > 0) {
                    keepTransforming = false;
                  }
                } else {
                  index = +p1 + +p2;
                  if (+p1 > 0) {
                    keepTransforming = false;
                  }
                }

                previousIteration = null;
                if (index >= 0) {
                  preivousP1Index = +p1;
                  resultPath.push(index);
                } else {
                  // invalid transform
                  resultPath = null;
                  break;
                }
              } else {
                resultPath.push(p1);
              }
            } else {
              resultPath.push(p1);
            }
          } else if (p1 != null && p2 == null) {
            resultPath.push(p1);
            previousIteration = null;
          } else if (p1 == null && p2 != null && preivousP1Index === 0) {
            resultPath.push(p2);
            previousIteration = null;
          }
        }
      }
    }

    return resultPath;
  }
}
